/**
 * useOperationsAlerts — composes the Command Center Operations rail's
 * curated, criticals-only alert feed from three pillar sources.
 *
 * Per .agents/Tasks/High Priority/operations-priority-alerts-curation.md §7.1:
 *   1. Drift     — getConfigurationDrifts (Open + active statuses bundle)
 *   2. SHALL BPA — getComplianceViolations (criticality=SHALL, status=Open)
 *   3. Identity  — getUserInsights (state-shaped, no event timestamps)
 *
 * Plus two count-only queries to drive the §10 Q1 footer link (number of
 * non-critical drifts also open):
 *   4. High-only drifts count (limit=1, used for .total)
 *   5. Medium-only drifts count (limit=1, used for .total)
 *
 * The active-drifts query reuses the same key as PillarCompliance and
 * useOperationsData (`pillar-compliance-active-drifts`) so opening Posture
 * before Operations doesn't refetch.
 *
 * Pure mapping/sort/cap logic lives in `@/lib/operations-alerts` so it can
 * be unit-tested without React.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { DriftSeverity } from '@/types'
import {
  composeOperationsAlerts,
  type OperationsAlert,
} from '@/lib/operations-alerts'

// Same key as useOperationsData / PillarCompliance — keeps the cache shared
// so opening Operations after Posture fires no extra request.
const ACTIVE_DRIFT_QUERY_KEY_PREFIX = 'pillar-compliance-active-drifts'
const ACTIVE_DRIFT_STATUSES = 'New,Pending,PendingApproval,AwaitingRemediation'

// Distinct keys for the count-only queries that drive the footer link.
const HIGH_DRIFT_COUNT_KEY   = 'operations-alerts-high-drift-count'
const MEDIUM_DRIFT_COUNT_KEY = 'operations-alerts-medium-drift-count'

// 5-minute stale time mirrors PillarCompliance's drift query so we don't
// invalidate the shared cache by reading it with a different staleness.
const DRIFT_STALE_MS = 5 * 60 * 1000
const VIOLATION_STALE_MS = 30 * 1000
const INSIGHTS_STALE_MS = 5 * 60 * 1000

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

export interface UseOperationsAlertsResult {
  /** Curated, sorted, capped at 8. */
  alerts: OperationsAlert[]
  /**
   * Total number of admit-list rows before cap. Drives the badge:
   *   alerts.length === totalCandidates → render `{N}`
   *   alerts.length  <  totalCandidates → render `{cap} of {totalCandidates}`
   */
  totalCandidates: number
  /**
   * Per-source admit-list candidate counts (pre-cap). The sum equals
   * `totalCandidates`. Useful for diagnostics; the overflow footer reads
   * `hiddenCountsBySource` instead so labels reflect what's actually hidden.
   */
  candidateCountsBySource: { drift: number; shall: number; identity: number }
  /**
   * Per-source HIDDEN counts (pre-cap minus visible). Drives the criticals
   * overflow footer's per-source deep links so labels never double-count
   * rows already visible in the rail. The sum equals
   * `totalCandidates - alerts.length`.
   */
  hiddenCountsBySource: { drift: number; shall: number; identity: number }
  /**
   * Counts of non-critical drifts also Open. Drives the §10 Q1 footer link
   * "{high} High and {medium} Medium drifts also open — view in queue."
   */
  nonCriticalDriftCount: { high: number; medium: number }
  isLoading: boolean
  error?: Error
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

export function useOperationsAlerts(
  tenantId: string | undefined,
): UseOperationsAlertsResult {
  // ── 1. Active drifts (Open + active statuses) ────────────────────────
  // Shared key with PillarCompliance + useOperationsData. We pull all
  // severities; the mapper filters down to Critical + 14-day window.
  const driftsQuery = useQuery({
    queryKey: [ACTIVE_DRIFT_QUERY_KEY_PREFIX, tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        status: ACTIVE_DRIFT_STATUSES,
        page: 1,
        limit: 25,
      }),
    enabled: !!tenantId,
    staleTime: DRIFT_STALE_MS,
  })

  // ── 2. SHALL violations (Open) ───────────────────────────────────────
  // Distinct cache key from useComplianceViolations({pageSize:500}) on
  // purpose: we want a small SHALL-only payload for fast first paint.
  const shallQuery = useQuery({
    queryKey: ['operations-alerts-shall-open', tenantId],
    queryFn: () =>
      api.getComplianceViolations(tenantId!, {
        criticality: 'SHALL',
        status: 'Open',
        pageSize: 50,
      }),
    enabled: !!tenantId,
    staleTime: VIOLATION_STALE_MS,
  })

  // ── 3. Identity insights ─────────────────────────────────────────────
  // Use the same key as useUserInsights elsewhere in the app would —
  // the lightweight summary endpoint is cheap and identity is state-shaped.
  const insightsQuery = useQuery({
    queryKey: ['user-insights-summary', tenantId],
    queryFn: () => api.getUserInsights(tenantId!),
    enabled: !!tenantId,
    staleTime: INSIGHTS_STALE_MS,
  })

  // ── 4 + 5. Footer-link counts (limit=1 → tiny payload, take .total) ──
  const highCountQuery = useQuery({
    queryKey: [HIGH_DRIFT_COUNT_KEY, tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        severity: DriftSeverity.High,
        status: ACTIVE_DRIFT_STATUSES,
        page: 1,
        limit: 1,
      }),
    enabled: !!tenantId,
    staleTime: DRIFT_STALE_MS,
  })

  const mediumCountQuery = useQuery({
    queryKey: [MEDIUM_DRIFT_COUNT_KEY, tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId,
        severity: DriftSeverity.Medium,
        status: ACTIVE_DRIFT_STATUSES,
        page: 1,
        limit: 1,
      }),
    enabled: !!tenantId,
    staleTime: DRIFT_STALE_MS,
  })

  // ── Compose ──────────────────────────────────────────────────────────
  // Reach into the React Query data inside the memo so the dep array sees
  // the cached items reference (stable across renders when the query is
  // idle) rather than a fresh `?? []` array each call.
  const driftsData     = driftsQuery.data
  const violationsData = shallQuery.data
  const insightsData   = insightsQuery.data

  const { alerts, totalCandidates, candidateCountsBySource, hiddenCountsBySource } = useMemo(
    () =>
      composeOperationsAlerts({
        drifts:     driftsData?.items ?? [],
        violations: violationsData?.items ?? [],
        insights:   insightsData ?? null,
      }),
    [driftsData, violationsData, insightsData],
  )

  const nonCriticalDriftCount = {
    high:   highCountQuery.data?.total   ?? 0,
    medium: mediumCountQuery.data?.total ?? 0,
  }

  // Aggregate query state. Loading is true if any required query is loading
  // for the first time; secondary count queries don't block first paint of
  // the rail itself, but we surface them so the caller can show skeletons.
  const isLoading =
    !!tenantId &&
    (driftsQuery.isLoading || shallQuery.isLoading || insightsQuery.isLoading)

  // Surface the first non-undefined error. Each individual mapper degrades
  // to an empty list on failure (since we coalesce `.items ?? []`), so the
  // rail can render a partial state if one source fails — but the consumer
  // can choose to show the error banner anyway.
  const error =
    (driftsQuery.error as Error | undefined) ??
    (shallQuery.error as Error | undefined) ??
    (insightsQuery.error as Error | undefined)

  return {
    alerts,
    totalCandidates,
    candidateCountsBySource,
    hiddenCountsBySource,
    nonCriticalDriftCount,
    isLoading,
    error,
  }
}
